/** imports */
import * as React from 'react'
import { createRoot } from 'react-dom/client';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { preSubmitBusinessContact } from 'tsRoot/actions/midworks-freelance/contacts'
import { setCookieToHash, getValueFromCookie, deleteCookies } from 'tsRoot/actions/cookie'
import { notify } from 'tsRoot/actions/notify'

import { Input }     from 'tsRoot/components/form/input'
import { TextArea }  from 'tsRoot/components/form/textarea'
import { Button }    from 'tsRoot/components/form/button'
import { Checkbox }  from 'tsRoot/components/form/checkbox'
import { CheckboxList } from 'tsRoot/components/form/checkboxList'

import { convertJSONStringArrayToArray } from 'tsRoot/utils/json'

import { checkValidation } from 'tsRoot/validator/validatorBase'
import { useSnackbar } from 'notistack';

/** エクスポートするJSXのコンポーネント */
export const Main = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const pageName = 'ContactBusiness' // cookieのネームスペース分割に利用

  const [formError, setFormError]         = React.useState('')
  const [submitError, setSubmitError]     = React.useState('')
  const [alreadySubmit, setAlreadySubmit] = React.useState(false)

  // 別々のReactDOMでは、stateが更新のたびに最初の描画時の値に戻るのでフォームでstateは使わない
  let inputs = {
    // NOTE: フォームの入力値はcookie経由でバックエンドAPIに送信するため、配列型の値はJSON文字列に変換した値をcookieに設定しており、recruitmentTargetIds の value は文字列型として扱い、初期値は空文字列を設定しています。
    recruitmentTargetIds: { value: '', validation: { require: true } },
    companyName: {value: '', validation: {require: true, lengthMax: 122}},
    companyNameKana: {value: '', validation: {lengthMax: 122, kana: true}},
    userName: {value: '', validation: {require: true, lengthMax: 122}},
    userNameKana: {value: '', validation: {lengthMax: 122, kana: true}},
    department: {value: '', validation: {}},
    phoneNumber: {value: '', validation: {require: true, lengthMax: 20, numberAndHyphen: true}},
    email: {value: '', validation: {require: true, lengthMax: 122, email: true}},
    url: {value: '', validation: {lengthMax: 255, url: true}},
    content: {value: '', validation: {lengthMax: 3000}},
    agree: {value: '', validation: {}},
    formExpired: {value: false, validation: {}}
  }

  const initFormExpired = getValueFromCookie(`${pageName}-formExpired`) as any
  if(initFormExpired != 'false') deleteCookies(pageName) // 一定以上の時間が経ってからの再訪問時は値を消す
  setCookieToHash(inputs, pageName)

  async function submit() {
    setAlreadySubmit(true)

    if (!formErrorCheck()) return;

    if(!executeRecaptcha) {
      // rechapchaのセットアップができていない場合（token不備など）
      // この場合にエラーをそのまま出すのはかっこわるいので、メンテナンスと表示して問い合わせもらう
      setFormError('メンテナンス中です。運営事務局に問い合わせください。')
      return false
    }

    const reCaptchaToken = await executeRecaptcha();

    const rootElement = document.getElementById('be_form');
    const rootElementData = rootElement?.dataset;

    const contactParams = {
      contact: {
        contact_business: {
          recruitment_target_ids: convertJSONStringArrayToArray(inputs.recruitmentTargetIds.value),
          company_name: inputs.companyName.value,
          company_name_kana: inputs.companyNameKana.value,
          department: inputs.department.value,
          user_name: inputs.userName.value,
          user_name_kana: inputs.userNameKana.value,
          phone_number: inputs.phoneNumber.value,
          email: inputs.email.value,
          url: inputs.url.value,
          content: inputs.content.value,
          recaptcha_token: reCaptchaToken,
        },
        recaptcha_token: reCaptchaToken,
        preSubmit: rootElementData ? rootElementData.presubmit == 'true' : true
      },
      contact_business: {
        // NOTE: フォームの入力値はcookie経由でバックエンドAPIに送信するため、cookieに格納されているJSON文字列をJSON.parseし配列に変換した値を使用する
        recruitment_target_ids: convertJSONStringArrayToArray(inputs.recruitmentTargetIds.value),
        company_name: inputs.companyName.value,
        company_name_kana: inputs.companyNameKana.value,
        department: inputs.department.value,
        user_name: inputs.userName.value,
        user_name_kana: inputs.userNameKana.value,
        phone_number: inputs.phoneNumber.value,
        email: inputs.email.value,
        url: inputs.url.value,
        content: inputs.content.value,
      },
    }

    await preSubmitBusinessContact(
      contactParams,
      setSubmitError,
      // @ts-ignore
      document.getElementById('be_form')?.dataset.redirect,
      enqueueSnackbar
    )
  }



  const stringToBoolean = (value: string | null | undefined) => {
    if (!value) return false

    return value.toLowerCase() == 'true' || value == '1'
  }

  const formErrorCheck = () => {
    setCookieToHash(inputs, pageName)

    // 各入力フォームの値の保持期間よりも短い時間でフォームの有効期限を設定して一部の値が欠落する状態を防ぐ
    if(getValueFromCookie(`${pageName}-formExpired`) != 'false') {
      notify(enqueueSnackbar, 'フォームの有効期限が切れています。画面を再読み込みしてください。', 'error')
      return false
    }

    // 各入力フォームの値の保持期間よりも短い時間でフォームの有効期限を設定して一部の値が欠落する状態を防ぐ
    if(inputs.formExpired.value != false) {
      setFormError('フォームの有効期限が切れています。画面を更新して再度実行してください。')
      return false
    }

    if (checkValidation(inputs.companyName.value, inputs.companyName.validation).length > 0
          || checkValidation(inputs.companyNameKana.value, inputs.companyNameKana.validation).length > 0
          || checkValidation(inputs.userName.value, inputs.userName.validation).length > 0
          || checkValidation(inputs.userNameKana.value, inputs.userNameKana.validation).length > 0
          || checkValidation(inputs.department.value, inputs.department.validation).length > 0
          || checkValidation(inputs.phoneNumber.value, inputs.phoneNumber.validation).length > 0
          || checkValidation(inputs.email.value, inputs.email.validation).length > 0
          || checkValidation(inputs.url.value, inputs.url.validation).length > 0
          || checkValidation(inputs.content.value, inputs.content.validation).length > 0
          || checkValidation(convertJSONStringArrayToArray(inputs.recruitmentTargetIds.value), inputs.recruitmentTargetIds.validation).length > 0 // recruitmentTargetIds はJSON文字列(一度cookieに格納するためJSON文字列に変換しています)を配列型に変換した値に対してバリデーションチェックしています
    ) {
      setFormError('入力内容を確認してください')
      return false
    }

    // 利用規約の同意チェック
    if (inputs.agree.value != 'true') {
      setFormError('利用規約の同意が必要です')
      return false
    }

    setFormError('')
    return true
  }

  React.useEffect(() => {
    document.cookie = `${pageName}-formExpired=false; max-age=3600; path=/`
    setCookieToHash(inputs, pageName)
    formErrorCheck()

    const recruitmentTargetElement = document.getElementById('be_form_recruitment_target_checkbox')
    const recruitmentTargetElementData = recruitmentTargetElement?.dataset

    const companyNameElement = document.getElementById('be_form_company_name')
    const companyNameElementData = companyNameElement?.dataset

    const companyNameKanaElement = document.getElementById('be_form_company_name_kana')
    const companyNameKanaElementData = companyNameKanaElement?.dataset

    const departmentElement = document.getElementById('be_form_department')
    const departmentElementData = departmentElement?.dataset

    const userNameElement = document.getElementById('be_form_user_name')
    const userNameElementData = userNameElement?.dataset

    const userNameKanaElement = document.getElementById('be_form_user_name_kana')
    const userNameKanaElementData = userNameKanaElement?.dataset

    const phoneNumberElement = document.getElementById('be_form_phonenumber')
    const phoneNumberElementData = phoneNumberElement?.dataset

    const emailElement = document.getElementById('be_form_email')
    const emailElementData = emailElement?.dataset

    const urlElement = document.getElementById('be_form_url')
    const urlElementData = urlElement?.dataset

    const contentElement = document.getElementById('be_form_content')
    const contentElementData = contentElement?.dataset

    const checkElement = document.getElementById('be_form_check')
    const checkLableElement = document.getElementById('be_form_check_label')
    const checkElementData = checkElement?.dataset

    if (recruitmentTargetElement && recruitmentTargetElementData) {
      createRoot(recruitmentTargetElement).render(
        <CheckboxList
          pageName={pageName}
          inputName={'recruitmentTargetIds'}
          checkList={[
            { id: "1", name: "フリーランス" },
            { id: "2", name: "中途" },
            { id: "3", name: "派遣" },
            { id: "4", name: "新卒" },
          ]}
          errorCheck={formErrorCheck}
          pcNowrap={stringToBoolean(recruitmentTargetElementData.pcNowrap)}
        />
      );
    }

    if (companyNameElement && companyNameElementData) {
      createRoot(companyNameElement).render(
        <Input
          className={companyNameElementData.class || ''}
          pageName={pageName}
          inputName={"companyName"}
          type="text"
          errorCheckEnable={alreadySubmit}
          placeholder={companyNameElementData.placeholder || ''}
          errorMessageClass={companyNameElementData.errormessageclass || ''}
          errorFlug={companyNameElementData.errorflug || ''}
          validationRule={inputs.companyName.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if (companyNameKanaElement && companyNameKanaElementData) {
      createRoot(companyNameKanaElement).render(
        <Input
          className={companyNameKanaElementData.class || ''}
          pageName={pageName}
          inputName={"companyNameKana"}
          type="hidden"
          errorCheckEnable={alreadySubmit}
          placeholder={companyNameKanaElementData.placeholder || ''}
          errorMessageClass={companyNameKanaElementData.errormessageclass || ''}
          errorFlug={companyNameKanaElementData.errorflug || ''}
          validationRule={inputs.companyNameKana.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if (departmentElement && departmentElementData) {
      createRoot(departmentElement).render(
        <Input
          className={departmentElementData.class || ''}
          pageName={pageName}
          inputName={"department"}
          type="text"
          errorCheckEnable={alreadySubmit}
          placeholder={departmentElementData.placeholder || ''}
          errorMessageClass={departmentElementData.errormessageclass || ''}
          errorFlug={departmentElementData.errorflug || ''}
          validationRule={inputs.department.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if (userNameElement && userNameElementData) {
      createRoot(userNameElement).render(
        <Input
          className={userNameElementData.class || ''}
          pageName={pageName}
          inputName={"userName"}
          type="text"
          errorCheckEnable={alreadySubmit}
          placeholder={userNameElementData.placeholder || ''}
          errorMessageClass={userNameElementData.errormessageclass || ''}
          errorFlug={userNameElementData.errorflug || ''}
          validationRule={inputs.userName.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if (userNameKanaElement && userNameKanaElementData) {
      createRoot(userNameKanaElement).render(
        <Input
          className={userNameKanaElementData.class || ''}
          pageName={pageName}
          inputName={"userNameKana"}
          type="hidden"
          errorCheckEnable={alreadySubmit}
          placeholder={userNameKanaElementData.placeholder || ''}
          errorMessageClass={userNameKanaElementData.errormessageclass || ''}
          errorFlug={userNameKanaElementData.errorflug || ''}
          validationRule={inputs.userNameKana.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if (phoneNumberElement && phoneNumberElementData) {
      createRoot(phoneNumberElement).render(
        <Input
          className={phoneNumberElementData.class || ''}
          pageName={pageName}
          inputName={"phoneNumber"}
          type="text"
          errorCheckEnable={alreadySubmit}
          placeholder={phoneNumberElementData.placeholder || ''}
          errorMessageClass={phoneNumberElementData.errormessageclass || ''}
          errorFlug={phoneNumberElementData.errorflug || ''}
          validationRule={inputs.phoneNumber.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if(emailElement && emailElementData) {
      createRoot(emailElement).render(
        <Input
          className={emailElementData.class || ''}
          pageName={pageName}
          inputName={"email"}
          type="text"
          errorCheckEnable={alreadySubmit}
          placeholder={emailElementData.placeholder || ''}
          errorMessageClass={emailElementData.errormessageclass || ''}
          errorFlug={emailElementData.errorflug || ''}
          validationRule={inputs.email.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if(urlElement && urlElementData) {
      createRoot(urlElement).render(
        <Input
          className={urlElementData.class || ''}
          pageName={pageName}
          inputName={"url"}
          type="text"
          errorCheckEnable={alreadySubmit}
          placeholder={urlElementData.placeholder || 'https://mid-works.com'}
          errorMessageClass={urlElementData.errormessageclass || ''}
          errorFlug={urlElementData.errorflug || ''}
          validationRule={inputs.url.validation}
          errorCheck={formErrorCheck}
        />
      );
    }

    if(contentElement && contentElementData) {
      createRoot(contentElement).render(
        <TextArea
          className={contentElementData.class || ''}
          pageName={pageName}
          inputName={"content"}
          type="text"
          errorCheckEnable={alreadySubmit}
          placeholder={contentElementData.placeholder || ''}
          errorMessageClass={contentElementData.errormessageclass || ''}
          errorFlug={contentElementData.errorflug || ''}
          validationRule={inputs.content.validation}
          errorCheck={formErrorCheck}

          rows={5}
        />
      );
    }

    if(checkElement && checkElementData) {
      createRoot(checkElement).render(
        <Checkbox
          className={checkElementData.class || ''}
          pageName={pageName}
          errorMessageClass={checkElementData.errormessageclass || ''}
          inputName="agree"
          inputElements={checkElement}
          labelElements={checkLableElement}
          errorCheck={formErrorCheck}
        />
      );
    }
  }, []);

  // recaptchaの取得関数が非同期で変数に格納されるため、submitボタンはその取得を待ってから描画する
  React.useEffect(() => {
    const submitElement = document.getElementById('be_form_submit')
    const submitElementData = submitElement?.dataset

    if(submitElement && submitElementData && executeRecaptcha && formError == ''){
      createRoot(submitElement).render(
        <Button
          onClick={() => submit()}
          className={submitElementData.class || ''}
          buttonName="submit"
          buttonText={submitElementData.value || ''}
          type='button'
          active={true}
        />
      );
    } else if (submitElement && submitElementData && executeRecaptcha) {
      createRoot(submitElement).render(
        <Button
          onClick={() => submit()}
          className={submitElementData.class || ''}
          buttonName="submit"
          buttonText={submitElementData.value || ''}
          type='button'
          active={false}
        />
      );
    }

  }, [executeRecaptcha, formError]);

  // useEffectのReactDOMでviewの一部分だけ書き換えるようにreactを使っているため、ここではreturnを空で返す
  return (<></>);
}
